<template>
    <div class="p-col-12">
        <div class="card">
            <page-header title="dataTable.reports.protocol-control.title" :actions="pageActions"/>
            <ancestryline-data-table :data-function="dataFunction" :columns="columns" :default-filter="filters" ref="protocolcontrolreportdatatable"/>
        </div>
    </div>
</template>

<script>
    import AncestrylineDataTable from "@/components/table/AncestrylineDataTable";
    import PageHeader from "@/components/page/PageHeader";
    import DialogService from "@/business/ModalBusiness";
    import ProtocolBusiness from "@/business/ProtocolBusiness";
    import ModalProtocolFilter from "@/viewer/protocol/ModalProtocolFilter";
    import ModalChecklistPendingCertificates from "@/viewer/reports/ModalChecklistPendingCertificates";
    import ChecklistStatusBusiness from "@/business/ChecklistStatusBusiness";
    import StageBusiness from "@/business/StageBusiness";
    import {ACTIVE} from "@/constants/StatusProtocol";

    export default {
        name: "ProtocolControlReport",
        components: {PageHeader, AncestrylineDataTable},

        data() {
            return {
                dataFunction: ProtocolBusiness.pagination,
                filters: {name: null, code: null, family_name: null, person_id: null, budget: false, status: ACTIVE, stage_id: null, current_user: null, type_service_id: null},
                columns: [
                    {field: "code", header: 'dataTable.protocol.columns.protocol', sortable: true, type: 'actions', customFunction: this.protocolName, style: 'width: 140px',
                        actions: [
                            {type: 'protocolLink', title: 'dataTable.protocol.columns.protocol', newTab: true},
                        ]
                    },
                    {field: "familyName", header: 'forms.protocol.family-name', sortable: true, style: 'width: 200px'},
                    {field: "mainClientName", header: 'dataTable.protocol.columns.main-client', sortable: true, style: 'width: 200px', type: 'actions',
                        actions: [
                            {type: "mainClientLink", title: 'dataTable.client.columns.name'},
                        ]
                    },
                    {field: "stageName", header: 'forms.protocol.stage', sortable: true, style: 'width: 120px', html: true, customFunction: this.stageNameLabel},
                    {field: "currentUserName", header: 'forms.protocol.current-user', sortable: true, style: 'width: 200px'},
                    {field: "typeServiceName", header: 'forms.protocol.type-service', sortable: true, style: 'width: 240px'},
                    {field: "hasChecklist", header: 'dataTable.reports.protocol-control.checklist-status', type: 'actions', html: true,
                        customFunction: this.checklistStatusLabel,
                        actions: [
                            {icon: 'pi pi-list-check', title: 'dataTable.reports.protocol-control.checklist-status', function: this.openChecklistStatusModal, visible: (data) => data.hasChecklist,
                                style: 'width: 100%; display: flex; align-items: flex-start; gap: 0.5rem;'},
                        ]
                    },
                ],
                pageActions: [
                    {icon: 'pi pi-filter', label: 'dataTable.protocol.filters', function: this.openFilterModal, type: 'button'},
                ],
            }
        },

        mounted(){
            this.$watch(
                () => this.$refs.protocolcontrolreportdatatable && this.$refs.protocolcontrolreportdatatable.dataList,
                (dataList) => this.loadChecklistStatus(dataList),
            );
        },

        methods: {

            protocolName: ProtocolBusiness.protocolName,

            stageNameLabel(protocol){
                return StageBusiness.buildColorTagHtml(protocol.stageColor, protocol.stageName);
            },

            checklistStatusLabel(protocol){
                if(!protocol.hasChecklist){
                    return '';
                }
                if(protocol.checklistPendingPersons === undefined){
                    return '...';
                }
                const progressBar = ChecklistStatusBusiness.buildProgressBarHtml(protocol.checklistProgress);
                if(protocol.checklistPendingPersons.length === 0){
                    return `${progressBar}${this.$t('dataTable.reports.protocol-control.checklist-status-empty')}`;
                }
                return progressBar + ChecklistStatusBusiness.summarizePendingPersons(protocol.checklistPendingPersons);
            },

            async loadChecklistStatus(dataList){
                if(!Array.isArray(dataList)){
                    return;
                }
                for(const protocol of dataList){
                    if(protocol.hasChecklist){
                        const {progress, pendingPersons} = await ChecklistStatusBusiness.getChecklistStatus(protocol.id);
                        this.$set(protocol, 'checklistProgress', progress);
                        this.$set(protocol, 'checklistPendingPersons', pendingPersons);
                    }
                }
            },

            async openChecklistStatusModal(protocol){
                await DialogService.openModal(ModalChecklistPendingCertificates, {protocolId: protocol.id, pendingPersons: protocol.checklistPendingPersons || null});
            },

            async openFilterModal(){
                const filters = await DialogService.openModal(ModalProtocolFilter, {filters: this.filters});
                if(filters){
                    this.$set(this.$refs.protocolcontrolreportdatatable, 'filters', filters);
                    this.$set(this, 'filters', filters);
                    await this.$refs.protocolcontrolreportdatatable.loadPage();
                }
            },

        }

    }
</script>

<style scoped>

    .card ::v-deep .checklist-status-list {
        font-size: 0.75rem;
        margin: 0;
        padding-left: 1rem;
    }

    .card ::v-deep .checklist-status-list ul {
        margin: 0;
        padding-left: 1rem;
    }

    .card ::v-deep .checklist-status-progress {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.75rem;
        margin-bottom: 0.5rem;
    }

    .card ::v-deep .checklist-status-progress-bar {
        flex: 1;
        min-width: 60px;
        height: 6px;
        border-radius: 3px;
        background-color: #e9ecef;
        overflow: hidden;
    }

    .card ::v-deep .checklist-status-progress-fill {
        height: 100%;
    }

    .card ::v-deep .stage-color-tag {
        white-space: normal;
        word-break: break-word;
    }

</style>
